import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { toast } from 'sonner';
import { productsApi, categoriesApi, checklistsApi, uploadApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { ProductSettings } from '@/components/admin/ProductSettings';
import { 
  Package, 
  MagnifyingGlass, 
  Plus, 
  Calendar,
  Barcode,
  ArrowClockwise,
  PlusCircle,
  Tag,
  ArrowSquareOut,
  UploadSimple,
  Trash,
  PencilSimple,
  FileCsv,
  CopySimple,
  CheckCircle,
  XCircle,
  Check,
  User,
  Phone,
  ShieldCheck
} from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';

export const AdminProducts = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('catalog');

  // Registrations Tab State
  const [regData, setRegData] = useState({ summary: null, registrations: [] });
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [regSearchQuery, setRegSearchQuery] = useState('');
  const [regSelectedCategory, setRegSelectedCategory] = useState('all');
  const [regWarrantyFilter, setRegWarrantyFilter] = useState('all');
  
  // Creation Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [modelId, setModelId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [manufactureDate, setManufactureDate] = useState('');
  const [warrantyExpiry, setWarrantyExpiry] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [purchaseUrl, setPurchaseUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editModelId, setEditModelId] = useState('');
  const [editSerialNumber, setEditSerialNumber] = useState('');
  const [editManufactureDate, setEditManufactureDate] = useState('');
  const [editWarrantyExpiry, setEditWarrantyExpiry] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editPurchaseUrl, setEditPurchaseUrl] = useState('');
  const [updating, setUpdating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Model Management State
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [newModelCategoryId, setNewModelCategoryId] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [newModelNumber, setNewModelNumber] = useState('');
  const [newModelDescription, setNewModelDescription] = useState('');
  const [newModelImageUrl, setNewModelImageUrl] = useState('');
  const [newModelPurchaseUrl, setNewModelPurchaseUrl] = useState('');
  const [newModelWarrantyMonths, setNewModelWarrantyMonths] = useState('');
  const [newModelAllowOutOfWarrantyTickets, setNewModelAllowOutOfWarrantyTickets] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [editingModelId, setEditingModelId] = useState(null);
  const [isEditModelModalOpen, setIsEditModelModalOpen] = useState(false);
  const [editModelName, setEditModelName] = useState('');
  const [editModelNumber, setEditModelNumber] = useState('');
  const [editModelDescription, setEditModelDescription] = useState('');
  const [editModelCategoryId, setEditModelCategoryId] = useState('');
  const [editModelImageUrl, setEditModelImageUrl] = useState('');
  const [editModelPurchaseUrl, setEditModelPurchaseUrl] = useState('');
  const [editModelWarrantyMonths, setEditModelWarrantyMonths] = useState('');
  const [editModelAllowOutOfWarrantyTickets, setEditModelAllowOutOfWarrantyTickets] = useState(false);

  // Batch Import State
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchStep, setBatchStep] = useState(1);
  const [batchCategoryId, setBatchCategoryId] = useState('');
  const [batchModelId, setBatchModelId] = useState('');
  const [batchRawText, setBatchRawText] = useState('');
  const [batchItems, setBatchItems] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [validatingBatch, setValidatingBatch] = useState(false);
  const [importingBatch, setImportingBatch] = useState(false);
  const [batchIngestMethod, setBatchIngestMethod] = useState('auto'); // always auto now

  // Sequence Generator State
  const [seqPrefix, setSeqPrefix] = useState('');
  const [seqStartIndex, setSeqStartIndex] = useState(1);
  const [seqQuantity, setSeqQuantity] = useState(10);
  const [seqPadding, setSeqPadding] = useState('001'); // 'none', '01', '001', '0001'
  const [seqManufactureDate, setSeqManufactureDate] = useState(new Date().toISOString().split('T')[0]);
  const [seqWarrantyExpiry, setSeqWarrantyExpiry] = useState('');

  // View Batch Modal State
  const [isViewBatchModalOpen, setIsViewBatchModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSearchQuery, setBatchSearchQuery] = useState('');
  const [batchModelSearch, setBatchModelSearch] = useState('');

  // Auto-clear models selection on category change
  useEffect(() => {
    setModelId('');
  }, [categoryId]);

  useEffect(() => {
    setEditModelId('');
  }, [editCategoryId]);

  useEffect(() => {
    setBatchModelId('');
  }, [batchCategoryId]);

  useEffect(() => {
    if (batchModelId) {
      const selectedModel = models.find(m => m.id === batchModelId);
      if (selectedModel) {
        let baseCode = '';
        if (selectedModel.model_number) {
          baseCode = selectedModel.model_number.replace(/[^A-Za-z0-9_\.-]/g, '').substring(0, 15).toUpperCase();
        } else {
          baseCode = selectedModel.name.replace(/[^A-Za-z0-9_\.-]/g, '').substring(0, 15).toUpperCase();
        }
        
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy = today.getFullYear();
        const datePart = `${dd}${mm}${yyyy}`;
        
        setSeqPrefix(`${baseCode}-${datePart}-`);
      }
    } else {
      setSeqPrefix('');
    }
  }, [batchModelId, models]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch products, categories, & models
      const [prodRes, catRes, modelRes] = await Promise.all([
        productsApi.listGlobal(),
        productsApi.getCategories(),
        productsApi.listModels()
      ]);
      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
      setModels(modelRes.data || []);
    } catch (error) {
      console.error('Failed to fetch product catalog:', error);
      toast.error('Failed to load product registry catalog');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    setLoadingRegs(true);
    try {
      const { data } = await productsApi.getRegistrationReport();
      setRegData(data || { summary: null, registrations: [] });
    } catch (err) {
      console.error('Failed to fetch registration report:', err);
      toast.error('Failed to load product registrations report');
    } finally {
      setLoadingRegs(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'registrations') {
      fetchRegistrations();
    }
  }, [activeTab]);

  const handleUnregister = async (registrationId, serialNumber, customerName) => {
    if (!confirm(`Are you sure you want to unregister serial number "${serialNumber}" from ${customerName}?`)) return;
    try {
      await productsApi.unregisterProduct(registrationId);
      toast.success(`Unregistered ${serialNumber} successfully`);
      fetchRegistrations();
    } catch (err) {
      console.error('Unregister failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to unregister product');
    }
  };

  const filteredRegistrations = (regData.registrations || []).filter(reg => {
    const q = regSearchQuery.toLowerCase().trim();
    const matchesSearch = !q || (
      reg.serial_number?.toLowerCase().includes(q) ||
      reg.model_name?.toLowerCase().includes(q) ||
      reg.customer_name?.toLowerCase().includes(q) ||
      reg.customer_phone?.toLowerCase().includes(q) ||
      reg.customer_email?.toLowerCase().includes(q)
    );
    const matchesCategory = regSelectedCategory === 'all' || (
      categories.find(c => c.name === reg.category_name)?.id === regSelectedCategory ||
      reg.category_name === categories.find(c => c.id === regSelectedCategory)?.name
    );
    const matchesWarranty = regWarrantyFilter === 'all' || reg.warranty_status === regWarrantyFilter;

    return matchesSearch && matchesCategory && matchesWarranty;
  });

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!categoryId || !serialNumber || !modelId) {
      toast.error('Please fill out all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        category_id: categoryId,
        serial_number: serialNumber.trim(),
        model_id: modelId,
      };
      if (manufactureDate) payload.manufacture_date = manufactureDate;
      if (warrantyExpiry) payload.warranty_expiry = warrantyExpiry;
      if (imageUrl.trim()) payload.image_url = imageUrl.trim();
      if (purchaseUrl.trim()) payload.purchase_url = purchaseUrl.trim();
      
      const { data } = await productsApi.createGlobal(payload);
      setProducts([data, ...products]);
      toast.success('Product added to registry catalog!');
      
      // Reset Form & Close
      setCategoryId('');
      setSerialNumber('');
      setModelId('');
      setManufactureDate('');
      setWarrantyExpiry('');
      setImageUrl('');
      setPurchaseUrl('');
      setSubmitAttempted(false);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error(error.response?.data?.detail || 'Failed to add product to catalog');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditModal = (product) => {
    setEditingProductId(product.id);
    setEditCategoryId(product.category_id);
    setEditSerialNumber(product.serial_number);
    setEditModelId(product.model_id || '');
    setEditManufactureDate(product.manufacture_date || '');
    setEditWarrantyExpiry(product.warranty_expiry || '');
    setEditImageUrl(product.image_url || '');
    setEditPurchaseUrl(product.purchase_url || '');
    setIsEditModalOpen(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!editCategoryId || !editSerialNumber || !editModelId) {
      toast.error('Please fill out all required fields');
      return;
    }

    setUpdating(true);
    try {
      const payload = {
        category_id: editCategoryId,
        serial_number: editSerialNumber.trim(),
        model_id: editModelId,
        manufacture_date: editManufactureDate || null,
        warranty_expiry: editWarrantyExpiry || null,
        image_url: editImageUrl.trim() || null,
        purchase_url: editPurchaseUrl.trim() || null,
      };

      const { data } = await productsApi.updateGlobal(editingProductId, payload);
      setProducts(products.map(p => p.id === editingProductId ? data : p));
      toast.success('Product registry catalog updated!');
      setSubmitAttempted(false);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    if (!newModelCategoryId || !newModelName) {
      toast.error('Please select a category and enter a model name');
      return;
    }
    setSavingModel(true);
    try {
      const payload = {
        category_id: newModelCategoryId,
        name: newModelName.trim(),
        model_number: newModelNumber.trim() || null,
        description: newModelDescription.trim() || null,
        image_url: newModelImageUrl.trim() || null,
        purchase_url: newModelPurchaseUrl.trim() || null,
        warranty_months: newModelWarrantyMonths ? parseInt(newModelWarrantyMonths, 10) : null,
        allow_out_of_warranty_tickets: newModelAllowOutOfWarrantyTickets
      };
      const { data } = await productsApi.createModel(payload);
      setModels([data, ...models]);
      toast.success('Product model created!');
      setIsModelModalOpen(false);
      setNewModelImageUrl('');
      setNewModelPurchaseUrl('');
      setNewModelWarrantyMonths('');
      setNewModelAllowOutOfWarrantyTickets(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to create product model');
    } finally {
      setSavingModel(false);
    }
  };

  const handleUpdateModel = async (e) => {
    e.preventDefault();
    if (!editModelCategoryId || !editModelName) {
      toast.error('Please select a category and enter a model name');
      return;
    }
    setSavingModel(true);
    try {
      const payload = {
        category_id: editModelCategoryId,
        name: editModelName.trim(),
        model_number: editModelNumber.trim() || null,
        description: editModelDescription.trim() || null,
        image_url: editModelImageUrl.trim() || null,
        purchase_url: editModelPurchaseUrl.trim() || null,
        warranty_months: editModelWarrantyMonths ? parseInt(editModelWarrantyMonths, 10) : null,
        allow_out_of_warranty_tickets: editModelAllowOutOfWarrantyTickets
      };
      const { data } = await productsApi.updateModel(editingModelId, payload);
      setModels(models.map(m => m.id === editingModelId ? data : m));
      toast.success('Product model updated!');
      setIsEditModelModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to update product model');
    } finally {
      setSavingModel(false);
    }
  };

  const handleParseBatch = () => {
    let items = [];
    if (batchIngestMethod === 'paste') {
      if (!batchRawText.trim()) {
        toast.error('Please paste some data first');
        return;
      }
      const lines = batchRawText.split(/\r?\n/);
      let startIndex = 0;
      if (lines.length > 0 && (lines[0].toLowerCase().includes('serial') || lines[0].toLowerCase().includes('number'))) {
        startIndex = 1;
      }
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const columns = line.split('\t');
        const serial = columns[0] ? columns[0].trim() : '';
        const manDate = columns[1] ? columns[1].trim() : '';
        const warrDate = columns[2] ? columns[2].trim() : '';
        if (serial) {
          items.push({
            serial_number: serial,
            manufacture_date: manDate || null,
            warranty_expiry: warrDate || null
          });
        }
      }
    } else if (batchIngestMethod === 'csv') {
      if (!batchRawText.trim()) {
        toast.error('Please upload/read a CSV file first');
        return;
      }
      const lines = batchRawText.split(/\r?\n/);
      let startIndex = 0;
      if (lines.length > 0 && (lines[0].toLowerCase().includes('serial') || lines[0].toLowerCase().includes('number'))) {
        startIndex = 1;
      }
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const columns = line.split(',');
        const serial = columns[0] ? columns[0].replace(/"/g, '').trim() : '';
        const manDate = columns[1] ? columns[1].replace(/"/g, '').trim() : '';
        const warrDate = columns[2] ? columns[2].replace(/"/g, '').trim() : '';
        if (serial) {
          items.push({
            serial_number: serial,
            manufacture_date: manDate || null,
            warranty_expiry: warrDate || null
          });
        }
      }
    } else {
      // Grid method: filter out rows with empty serials
      items = batchItems.filter(item => item.serial_number?.trim());
    }

    if (items.length === 0) {
      toast.error('No valid product rows parsed. Please check your inputs.');
      return;
    }

    setBatchItems(items);
    setBatchStep(3);
    runBatchValidation(items);
  };

  const handleGenerateSequence = () => {
    if (!seqPrefix.trim()) {
      toast.error('Please enter a sequence prefix');
      return;
    }
    const qty = parseInt(seqQuantity, 10);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    const startIdx = parseInt(seqStartIndex, 10);
    if (isNaN(startIdx) || startIdx < 0) {
      toast.error('Please enter a valid starting index');
      return;
    }

    const items = [];
    for (let i = 0; i < qty; i++) {
      const currentNumber = startIdx + i;
      let suffix = '';
      if (seqPadding === 'none') {
        suffix = String(currentNumber);
      } else if (seqPadding === '01') {
        suffix = String(currentNumber).padStart(2, '0');
      } else if (seqPadding === '001') {
        suffix = String(currentNumber).padStart(3, '0');
      } else if (seqPadding === '0001') {
        suffix = String(currentNumber).padStart(4, '0');
      }
      
      items.push({
        serial_number: `${seqPrefix.trim()}${suffix}`,
        manufacture_date: seqManufactureDate || null,
        warranty_expiry: seqWarrantyExpiry || null
      });
    }

    setBatchItems(items);
    setBatchStep(3);
    runBatchValidation(items);
  };

  const runBatchValidation = async (itemsToValidate) => {
    setValidatingBatch(true);
    try {
      const { data } = await productsApi.batchValidate({
        model_id: batchModelId,
        items: itemsToValidate.map(item => ({
          serial_number: item.serial_number,
          manufacture_date: item.manufacture_date || null,
          warranty_expiry: item.warranty_expiry || null
        }))
      });
      setValidationResults(data.items || []);
    } catch (error) {
      console.error(error);
      toast.error('Batch validation failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setValidatingBatch(false);
    }
  };

  const handleUpdateBatchItemCell = (index, field, value) => {
    const updated = [...batchItems];
    updated[index][field] = value;
    setBatchItems(updated);
  };

  const handleRemoveBatchItem = (index) => {
    const updatedItems = batchItems.filter((_, i) => i !== index);
    const updatedResults = validationResults.filter((_, i) => i !== index);
    setBatchItems(updatedItems);
    setValidationResults(updatedResults);
  };

  const handleCommitBatch = async () => {
    const hasErrors = validationResults.some(res => !res.is_valid);
    if (hasErrors) {
      toast.error('Please resolve all validation errors before importing.');
      return;
    }

    setImportingBatch(true);
    try {
      await productsApi.batchCreate({
        model_id: batchModelId,
        items: batchItems.map(item => ({
          serial_number: item.serial_number.trim(),
          manufacture_date: item.manufacture_date || null,
          warranty_expiry: item.warranty_expiry || null
        }))
      });
      toast.success('Successfully imported all products!');
      setBatchStep(4);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to import batch: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setImportingBatch(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product from the registry?')) return;
    try {
      await productsApi.deleteGlobal(productId);
      setProducts(products.filter(p => p.id !== productId));
      if (selectedBatch) {
        setSelectedBatch({
          ...selectedBatch,
          products: selectedBatch.products.filter(p => p.id !== productId)
        });
      }
      toast.success('Product deleted from catalog registry');
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const groupProductsIntoBatches = (prods) => {
    if (!prods || prods.length === 0) return [];
    
    // Sort products by created_at descending (newest first)
    const sorted = [...prods].sort((a, b) => {
      const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tB - tA;
    });
    
    const batches = [];
    let currentBatch = null;
    
    sorted.forEach(prod => {
      const prodTime = prod.created_at ? new Date(prod.created_at).getTime() : 0;
      
      if (!currentBatch) {
        currentBatch = {
          id: prod.id,
          model_id: prod.model_id,
          model_name: prod.model_name,
          category_id: prod.category_id,
          manufacture_date: prod.manufacture_date,
          warranty_expiry: prod.warranty_expiry,
          image_url: prod.image_url,
          purchase_url: prod.purchase_url,
          created_at: prod.created_at,
          products: [prod],
          min_time: prodTime,
          max_time: prodTime
        };
      } else {
        const isSameModel = prod.model_id === currentBatch.model_id;
        const isSameManDate = prod.manufacture_date === currentBatch.manufacture_date;
        const isWithinTimeWindow = Math.abs(prodTime - currentBatch.max_time) <= 60000 || Math.abs(prodTime - currentBatch.min_time) <= 60000;
        
        if (isSameModel && isSameManDate && isWithinTimeWindow) {
          currentBatch.products.push(prod);
          currentBatch.min_time = Math.min(currentBatch.min_time, prodTime);
          currentBatch.max_time = Math.max(currentBatch.max_time, prodTime);
        } else {
          batches.push(currentBatch);
          currentBatch = {
            id: prod.id,
            model_id: prod.model_id,
            model_name: prod.model_name,
            category_id: prod.category_id,
            manufacture_date: prod.manufacture_date,
            warranty_expiry: prod.warranty_expiry,
            image_url: prod.image_url,
            purchase_url: prod.purchase_url,
            created_at: prod.created_at,
            products: [prod],
            min_time: prodTime,
            max_time: prodTime
          };
        }
      }
    });
    
    if (currentBatch) {
      batches.push(currentBatch);
    }
    
    return batches;
  };

  const handleDeleteBatch = async (batch) => {
    if (!confirm(`Are you sure you want to delete this batch containing ${batch.products.length} products?`)) return;
    
    try {
      const productIds = batch.products.map(p => p.id);
      await productsApi.batchDelete({ product_ids: productIds });
      setProducts(products.filter(p => !productIds.includes(p.id)));
      toast.success('Batch deleted successfully!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete batch: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : 'Unknown Category';
  };

  const productBatches = groupProductsIntoBatches(products);

  const filteredBatches = productBatches.filter(batch => {
    const query = searchQuery.toLowerCase();
    
    const matchesSearch = 
      batch.model_name?.toLowerCase().includes(query) ||
      batch.products.some(p => p.serial_number?.toLowerCase().includes(query));
      
    const matchesCategory = 
      selectedCategory === 'all' || batch.category_id === selectedCategory;
      
    return matchesSearch && matchesCategory;
  });

  return (
    <DashboardLayout role="super_admin">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight mb-2">Product Catalog</h1>
            <p className="text-muted-foreground">Manage products, categories, models, and service checklists</p>
          </div>
          {activeTab === 'catalog' && (
            <div className="flex gap-2">
              <Button 
                onClick={() => {
                  setBatchCategoryId('');
                  setBatchModelId('');
                  setBatchRawText('');
                  setBatchItems([]);
                  setValidationResults([]);
                  setBatchStep(1);
                  setBatchModelSearch('');
                  setIsBatchModalOpen(true);
                }}
                variant="outline"
                className="font-bold border-slate-350 hover:bg-slate-50 flex items-center gap-1.5"
              >
                <UploadSimple size={18} weight="bold" /> Batch Import
              </Button>
              <Button 
                onClick={() => setIsAddModalOpen(true)}
                className="font-bold text-white hover:opacity-90 flex items-center gap-1.5 shadow"
                style={{ backgroundColor: brandColorAccent }}
              >
                <Plus size={18} weight="bold" /> Add Product
              </Button>
            </div>
          )}
          {activeTab === 'models' && (
            <Button 
              onClick={() => {
                setEditingModelId(null);
                setNewModelCategoryId('');
                setNewModelName('');
                setNewModelNumber('');
                setNewModelDescription('');
                setNewModelImageUrl('');
                setNewModelPurchaseUrl('');
                setNewModelWarrantyMonths('');
                setNewModelAllowOutOfWarrantyTickets(false);
                setIsModelModalOpen(true);
              }}
              className="font-bold text-white hover:opacity-90 flex items-center gap-1.5 shadow"
              style={{ backgroundColor: brandColorAccent }}
            >
              <Plus size={18} weight="bold" /> Add Model
            </Button>
          )}
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 border-b border-slate-200 mb-2">
          {['catalog', 'registrations', 'models', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-sm font-bold capitalize transition-colors border-b-2 ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Registrations Tab */}
        {activeTab === 'registrations' && (
          <div className="space-y-6 animate-fade-in">
            {/* Stats Overview Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-5 border bg-white shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 font-bold">
                  <Package size={24} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Products</p>
                  <p className="text-2xl font-black text-slate-850">
                    {regData.summary?.total_products ?? products.length}
                  </p>
                </div>
              </Card>

              <Card className="p-5 border bg-emerald-50/50 border-emerald-100 shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">Registered</p>
                  <p className="text-2xl font-black text-emerald-700">
                    {regData.summary?.registered_products ?? regData.registrations.length}
                  </p>
                </div>
              </Card>

              <Card className="p-5 border bg-amber-50/50 border-amber-100 shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center font-bold">
                  <XCircle size={24} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Unregistered</p>
                  <p className="text-2xl font-black text-amber-700">
                    {regData.summary?.unregistered_products ?? Math.max(0, products.length - regData.registrations.length)}
                  </p>
                </div>
              </Card>

              <Card className="p-5 border bg-blue-50/50 border-blue-100 shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Registration Rate</p>
                  <p className="text-2xl font-black text-blue-700">
                    {regData.summary?.registration_rate ?? 0}%
                  </p>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <Card className="p-4 border grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
              <div className="relative md:col-span-2">
                <MagnifyingGlass 
                  size={20} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                />
                <Input
                  placeholder="Search by serial number, model, customer name, phone..."
                  value={regSearchQuery}
                  onChange={(e) => setRegSearchQuery(e.target.value)}
                  className="pl-10 h-10 border-slate-300 text-slate-900"
                />
              </div>

              <div>
                <Select value={regSelectedCategory} onValueChange={setRegSelectedCategory}>
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={regWarrantyFilter} onValueChange={setRegWarrantyFilter}>
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Warranty Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warranty Statuses</SelectItem>
                    <SelectItem value="Active">Active Warranty</SelectItem>
                    <SelectItem value="Expired">Expired Warranty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {/* Registration Table */}
            {loadingRegs ? (
              <LoadingSpinner />
            ) : filteredRegistrations.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                        <th className="py-4 px-6">Product Details</th>
                        <th className="py-4 px-6">Registered To</th>
                        <th className="py-4 px-6">Category</th>
                        <th className="py-4 px-6">Registration Date</th>
                        <th className="py-4 px-6">Warranty Status</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredRegistrations.map((reg) => (
                        <tr key={reg.registration_id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6">
                            <div className="font-bold text-slate-850 font-mono text-base">{reg.serial_number}</div>
                            <div className="text-xs font-semibold text-slate-500">{reg.model_name}</div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-bold text-slate-850 flex items-center gap-1.5">
                              <User size={16} className="text-slate-400" />
                              {reg.customer_name}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                              <Phone size={14} className="text-slate-400" />
                              {reg.customer_phone}
                            </div>
                          </td>
                          <td className="py-4 px-6 font-semibold text-slate-650">
                            {reg.category_name}
                          </td>
                          <td className="py-4 px-6 text-slate-500">
                            {reg.registered_at ? new Date(reg.registered_at).toLocaleString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }) : '—'}
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                              reg.warranty_status === 'Active'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                : 'bg-rose-100 text-rose-800 border border-rose-200'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${reg.warranty_status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                              {reg.warranty_status}
                              {reg.warranty_expiry && (
                                <span className="text-[11px] font-normal opacity-80"> ({formatDate(reg.warranty_expiry)})</span>
                              )}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="font-bold text-xs uppercase tracking-wider text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                              onClick={() => handleUnregister(reg.registration_id, reg.serial_number, reg.customer_name)}
                            >
                              Unregister
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center border">
                <CheckCircle size={48} className="mx-auto text-slate-350 mb-4" />
                <h2 className="text-xl font-bold mb-2">No product registrations match criteria</h2>
                <p className="text-muted-foreground">Adjust filters or register products for customers to view registration reports here.</p>
              </Card>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <ProductSettings />
        )}

        {/* Models Tab */}
        {activeTab === 'models' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Filters for Models */}
            <Card className="p-4 border flex gap-4 items-center">
              <div className="relative flex-1">
                <MagnifyingGlass 
                  size={20} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                />
                <Input
                  placeholder="Search models by name or number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 border-slate-300 text-slate-900"
                />
              </div>
            </Card>

            {loading ? (
              <LoadingSpinner />
            ) : models.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                        <th className="py-4 px-6">Model Name</th>
                        <th className="py-4 px-6">Model Number</th>
                        <th className="py-4 px-6">Category</th>
                        <th className="py-4 px-6">Warranty</th>
                        <th className="py-4 px-6">Description</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {models
                        .filter(m => {
                          const query = searchQuery.toLowerCase();
                          return (
                            (m.name || '').toLowerCase().includes(query) ||
                            (m.model_number || '').toLowerCase().includes(query) ||
                            getCategoryName(m.category_id)?.toLowerCase().includes(query)
                          );
                        })
                        .map((model) => (
                          <tr key={model.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="py-4 px-6 font-bold text-slate-850">
                              {model.name}
                            </td>
                            <td className="py-4 px-6 font-mono font-bold text-slate-800">
                              {model.model_number || '—'}
                            </td>
                            <td className="py-4 px-6 font-semibold text-slate-650">
                              {getCategoryName(model.category_id)}
                            </td>
                            <td className="py-4 px-6 text-slate-600 font-medium">
                              {model.warranty_months !== null && model.warranty_months !== undefined
                                ? `${model.warranty_months} months`
                                : 'Default (Category)'}
                            </td>
                            <td className="py-4 px-6 text-slate-500 max-w-xs truncate">
                              {model.description || '—'}
                            </td>
                            <td className="py-4 px-6 text-right space-x-2 whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                className="font-bold text-xs uppercase tracking-wider h-8"
                                onClick={() => {
                                  setEditingModelId(model.id);
                                  setEditModelCategoryId(model.category_id);
                                  setEditModelName(model.name);
                                  setEditModelNumber(model.model_number || '');
                                  setEditModelDescription(model.description || '');
                                  setEditModelImageUrl(model.image_url || '');
                                  setEditModelPurchaseUrl(model.purchase_url || '');
                                  setEditModelWarrantyMonths(model.warranty_months !== null && model.warranty_months !== undefined ? String(model.warranty_months) : '');
                                  setEditModelAllowOutOfWarrantyTickets(model.allow_out_of_warranty_tickets !== false);
                                  setIsEditModelModalOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="font-bold text-xs uppercase tracking-wider h-8 text-white"
                                onClick={async () => {
                                  if (!confirm('Are you sure you want to delete this model? Existing products of this model will remain but you won\'t be able to select it for new products.')) return;
                                  try {
                                    await productsApi.deleteModel(model.id);
                                    setModels(models.filter(m => m.id !== model.id));
                                    toast.success('Product model deleted successfully');
                                  } catch (error) {
                                    console.error('Failed to delete model:', error);
                                    toast.error(error.response?.data?.detail || 'Failed to delete product model');
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center border">
                <Package size={48} className="mx-auto text-slate-350 mb-4" />
                <h2 className="text-xl font-bold mb-2">No product models</h2>
                <p className="text-muted-foreground">Add model specifications under categories first.</p>
              </Card>
            )}
          </div>
        )}

        {/* Catalog Tab */}
        {activeTab === 'catalog' && (<>

        {/* Filters */}
        <Card className="p-4 border grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="relative md:col-span-2">
            <MagnifyingGlass 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
            />
            <Input
              placeholder="Search by serial number or model name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 border-slate-300 text-slate-900"
            />
          </div>

          <div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-10 border-slate-300">
                <SelectValue placeholder="Filter by Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Product Catalog List */}
        {loading ? (
          <LoadingSpinner />
        ) : filteredBatches.length > 0 ? (
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                    <th className="py-4 px-6">Batch Model</th>
                    <th className="py-4 px-6">Quantity</th>
                    <th className="py-4 px-6">Category</th>
                    <th className="py-4 px-6">Manufacture Date</th>
                    <th className="py-4 px-6">Warranty Expiry</th>
                    <th className="py-4 px-6">Import Date</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredBatches.map((batch) => (
                    <tr key={batch.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 px-6 font-bold text-slate-850 flex items-center gap-3">
                        {batch.image_url ? (
                          <img src={batch.image_url} alt={batch.model_name} className="w-10 h-10 object-contain rounded border bg-slate-50" />
                        ) : (
                          <div className="w-10 h-10 flex items-center justify-center bg-slate-50 border rounded text-slate-400">
                            <Package size={22} />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span>{batch.model_name}</span>
                            {batch.purchase_url && (
                              <a href={batch.purchase_url} target="_blank" rel="noopener noreferrer" className="text-indigo-650 hover:text-indigo-850 flex items-center inline-flex" title="View Purchase Link">
                                <ArrowSquareOut size={14} className="ml-1" />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-semibold text-slate-800">{batch.products.length} units</td>
                      <td className="py-4 px-6 font-semibold text-slate-650">{getCategoryName(batch.category_id)}</td>
                      <td className="py-4 px-6 text-slate-500">{batch.manufacture_date ? formatDate(batch.manufacture_date) : '—'}</td>
                      <td className="py-4 px-6 text-slate-500">{batch.warranty_expiry ? formatDate(batch.warranty_expiry) : '—'}</td>
                      <td className="py-4 px-6 text-slate-500">
                        {batch.created_at ? new Date(batch.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }) : '—'}
                      </td>
                      <td className="py-4 px-6 text-right space-x-2 whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="font-bold text-xs uppercase tracking-wider h-8"
                          onClick={() => {
                            setSelectedBatch(batch);
                            setBatchSearchQuery('');
                            setIsViewBatchModalOpen(true);
                          }}
                        >
                          View Products
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="font-bold text-xs uppercase tracking-wider h-8 text-white"
                          onClick={() => handleDeleteBatch(batch)}
                        >
                          Delete Batch
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <Card className="p-12 text-center border">
            <Package size={48} className="mx-auto text-slate-350 mb-4" />
            <h2 className="text-xl font-bold mb-2">No product batches in registry</h2>
            <p className="text-muted-foreground">Adjust filters or register new products to start tracking them.</p>
          </Card>
        )}

        {/* Add Product Modal */}
        <Dialog open={isAddModalOpen} onOpenChange={(open) => { setIsAddModalOpen(open); if(!open) setSubmitAttempted(false); }}>
          <DialogContent className="md:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Add Product to Registry</DialogTitle>
              <DialogDescription className="text-xs">
                Log a physical model and valid serial number in the global organization catalog.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateProduct} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Category */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Category *
                  </Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger className={`h-11 border-slate-300 ${submitAttempted && !categoryId ? 'border-red-500 focus:ring-red-500/20' : ''}`}>
                      <SelectValue placeholder="Select product category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Model */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Model *
                  </Label>
                  <Select value={modelId} onValueChange={setModelId} disabled={!categoryId}>
                    <SelectTrigger className={`h-11 border-slate-300 ${submitAttempted && !modelId ? 'border-red-500 focus:ring-red-500/20' : ''}`}>
                      <SelectValue placeholder={categoryId ? "Select product model..." : "Select category first..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {models
                        .filter((m) => m.category_id === categoryId)
                        .map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name} {model.model_number ? `(${model.model_number})` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {categoryId && models.filter((m) => m.category_id === categoryId).length === 0 && (
                    <p className="text-[10px] text-amber-600 font-medium">
                      No models found for this category. Please create one in the Models tab first.
                    </p>
                  )}
                </div>

                {/* Serial Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="serial-num" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Serial Number *
                  </Label>
                  <div className="relative">
                    <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="serial-num"
                      type="text"
                      placeholder="e.g. CL-2003"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      className={`pl-10 h-11 border-slate-300 font-mono text-slate-900 ${submitAttempted && !serialNumber ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      required
                    />
                  </div>
                </div>

                {/* Manufacture Date */}
                <div className="space-y-1.5">
                  <Label htmlFor="man-date" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Manufacture Date (Optional)
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="man-date"
                      type="date"
                      value={manufactureDate}
                      onChange={(e) => setManufactureDate(e.target.value)}
                      className="pl-10 h-11 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                {/* Warranty Expiry */}
                <div className="space-y-1.5">
                  <Label htmlFor="warr-exp" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Warranty Expiry (Optional)
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="warr-exp"
                      type="date"
                      value={warrantyExpiry}
                      onChange={(e) => setWarrantyExpiry(e.target.value)}
                      className="pl-10 h-11 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                {/* Image Uploader & Preview */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Image
                  </Label>
                  <div className="flex gap-4 items-center">
                    {imageUrl ? (
                      <div className="relative w-16 h-16 border rounded bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={imageUrl} alt="Preview" className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => setImageUrl('')}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 hover:bg-red-600 transition-colors"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 border-2 border-dashed bg-slate-50 border-slate-300 rounded flex items-center justify-center text-slate-400 shrink-0">
                        <Package size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        id="image-file-add"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingImage(true);
                          try {
                            const url = await uploadApi.uploadFile(file);
                            setImageUrl(url);
                            toast.success('Image uploaded successfully!');
                          } catch (err) {
                            console.error(err);
                            toast.error('Failed to upload image');
                          } finally {
                            setUploadingImage(false);
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('image-file-add').click()}
                        disabled={uploadingImage}
                        className="font-bold text-xs uppercase tracking-wider h-10 border-slate-300 bg-white"
                      >
                        <UploadSimple size={16} className="mr-1.5" />
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      <p className="text-[10px] text-slate-500 font-medium mt-1.5">
                        Accepts PNG, JPG, or GIF. Saves to Cloudflare R2 or local device.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="image-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Image URL (Optional)
                  </Label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="image-url"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      className="pl-10 h-11 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                {/* Purchase URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="purchase-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Purchase/Info URL (Optional)
                  </Label>
                  <div className="relative">
                    <ArrowSquareOut className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="purchase-url"
                      type="url"
                      placeholder="https://example.com/buy"
                      value={purchaseUrl}
                      onChange={(e) => setPurchaseUrl(e.target.value)}
                      className="pl-10 h-11 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsAddModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {submitting ? 'Saving...' : 'Add to Catalog'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Product Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={(open) => { setIsEditModalOpen(open); if(!open) setSubmitAttempted(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Edit Product Registry</DialogTitle>
              <DialogDescription className="text-xs">
                Update details for this catalog product model and serial number.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpdateProduct} className="space-y-4 mt-4">
              {/* Category */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Category *
                </Label>
                <Select value={editCategoryId} onValueChange={setEditCategoryId}>
                  <SelectTrigger className={`h-11 border-slate-300 text-slate-900 ${submitAttempted && !editCategoryId ? 'border-red-500 focus:ring-red-500/20' : ''}`}>
                    <SelectValue placeholder="Select product category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Product Model */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Model *
                </Label>
                <Select value={editModelId} onValueChange={setEditModelId} disabled={!editCategoryId}>
                  <SelectTrigger className={`h-11 border-slate-300 text-slate-900 ${submitAttempted && !editModelId ? 'border-red-500 focus:ring-red-500/20' : ''}`}>
                    <SelectValue placeholder={editCategoryId ? "Select product model..." : "Select category first..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {models
                      .filter((m) => m.category_id === editCategoryId)
                      .map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name} {model.model_number ? `(${model.model_number})` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {editCategoryId && models.filter((m) => m.category_id === editCategoryId).length === 0 && (
                  <p className="text-[10px] text-amber-600 font-medium">
                    No models found for this category. Please create one in the Models tab first.
                  </p>
                )}
              </div>

              {/* Serial Number */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-serial-num" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Serial Number *
                </Label>
                <div className="relative">
                  <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-serial-num"
                    type="text"
                    placeholder="e.g. CL-2003"
                    value={editSerialNumber}
                    onChange={(e) => setEditSerialNumber(e.target.value)}
                    className={`pl-10 h-11 border-slate-300 font-mono text-slate-900 ${submitAttempted && !editSerialNumber ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    required
                  />
                </div>
              </div>

              {/* Manufacture Date */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-man-date" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Manufacture Date (Optional)
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-man-date"
                    type="date"
                    value={editManufactureDate}
                    onChange={(e) => setEditManufactureDate(e.target.value)}
                    className="pl-10 h-11 border-slate-300 text-slate-900"
                  />
                </div>
              </div>

              {/* Warranty Expiry */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-warr-exp" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Warranty Expiry (Optional)
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-warr-exp"
                    type="date"
                    value={editWarrantyExpiry}
                    onChange={(e) => setEditWarrantyExpiry(e.target.value)}
                    className="pl-10 h-11 border-slate-300 text-slate-900"
                  />
                </div>
              </div>

              {/* Image Uploader & Preview (Edit) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Image
                </Label>
                <div className="flex gap-4 items-center">
                  {editImageUrl ? (
                    <div className="relative w-16 h-16 border rounded bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      <img src={editImageUrl} alt="Preview" className="w-full h-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setEditImageUrl('')}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 hover:bg-red-600 transition-colors"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 border-2 border-dashed bg-slate-50 border-slate-300 rounded flex items-center justify-center text-slate-400 shrink-0">
                      <Package size={24} />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      id="image-file-edit"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingImage(true);
                        try {
                          const url = await uploadApi.uploadFile(file);
                          setEditImageUrl(url);
                          toast.success('Image uploaded successfully!');
                        } catch (err) {
                          console.error(err);
                          toast.error('Failed to upload image');
                        } finally {
                          setUploadingImage(false);
                        }
                      }}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('image-file-edit').click()}
                      disabled={uploadingImage}
                      className="font-bold text-xs uppercase tracking-wider h-10 border-slate-300 bg-white"
                    >
                      <UploadSimple size={16} className="mr-1.5" />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    <p className="text-[10px] text-slate-500 font-medium mt-1.5">
                      Accepts PNG, JPG, or GIF. Saves to Cloudflare R2 or local device.
                    </p>
                  </div>
                </div>
              </div>

              {/* Image URL */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-image-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Image URL (Optional)
                </Label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-image-url"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={editImageUrl}
                    onChange={(e) => setEditImageUrl(e.target.value)}
                    className="pl-10 h-11 border-slate-300 text-slate-900"
                  />
                </div>
              </div>

              {/* Purchase URL */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-purchase-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Purchase/Info URL (Optional)
                </Label>
                <div className="relative">
                  <ArrowSquareOut className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-purchase-url"
                    type="url"
                    placeholder="https://example.com/buy"
                    value={editPurchaseUrl}
                    onChange={(e) => setEditPurchaseUrl(e.target.value)}
                    className="pl-10 h-11 border-slate-300 text-slate-900"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsEditModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updating}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </>)}

        {/* Add Model Modal */}
        <Dialog open={isModelModalOpen} onOpenChange={setIsModelModalOpen}>
          <DialogContent className="max-w-md md:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Add Product Model</DialogTitle>
              <DialogDescription className="text-xs">
                Define a reusable product model specification under a category.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateModel} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                {/* Category */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Category *
                  </Label>
                  <Select value={newModelCategoryId} onValueChange={setNewModelCategoryId}>
                    <SelectTrigger className="h-11 border-slate-300">
                      <SelectValue placeholder="Select product category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Model Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="new-model-name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Name *
                  </Label>
                  <Input
                    id="new-model-name"
                    type="text"
                    placeholder="e.g. WindMax 100L"
                    value={newModelName}
                    onChange={(e) => setNewModelName(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                    required
                  />
                </div>

                {/* Model Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="new-model-number" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Number (Optional)
                  </Label>
                  <Input
                    id="new-model-number"
                    type="text"
                    placeholder="e.g. WM-100L-2026"
                    value={newModelNumber}
                    onChange={(e) => setNewModelNumber(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Warranty Duration */}
                <div className="space-y-1.5">
                  <Label htmlFor="new-model-warranty-months" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Warranty Duration (Months) (Optional Override)
                  </Label>
                  <Input
                    id="new-model-warranty-months"
                    type="number"
                    min="0"
                    placeholder="Leave empty to use category default"
                    value={newModelWarrantyMonths}
                    onChange={(e) => setNewModelWarrantyMonths(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Image Uploader & Preview */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Image (Catalog Photo)
                  </Label>
                  <div className="flex gap-4 items-center">
                    {newModelImageUrl ? (
                      <div className="relative w-16 h-16 border rounded bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={newModelImageUrl} alt="Preview" className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => setNewModelImageUrl('')}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 hover:bg-red-600 transition-colors"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 border-2 border-dashed bg-slate-50 border-slate-300 rounded flex items-center justify-center text-slate-400 shrink-0">
                        <Package size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        id="model-image-file-add"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingImage(true);
                          try {
                            const url = await uploadApi.uploadFile(file);
                            setNewModelImageUrl(url);
                            toast.success('Model image uploaded successfully!');
                          } catch (err) {
                            console.error(err);
                            toast.error('Failed to upload model image');
                          } finally {
                            setUploadingImage(false);
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('model-image-file-add').click()}
                        disabled={uploadingImage}
                        className="font-bold text-xs uppercase tracking-wider h-10 border-slate-300 bg-white"
                      >
                        <UploadSimple size={16} className="mr-1.5" />
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      <p className="text-[10px] text-slate-500 font-medium mt-1.5">
                        Accepts PNG, JPG, or GIF.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Model Image URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="new-model-image-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Image URL (Optional override)
                  </Label>
                  <Input
                    id="new-model-image-url"
                    type="text"
                    placeholder="https://example.com/image.jpg"
                    value={newModelImageUrl}
                    onChange={(e) => setNewModelImageUrl(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Purchase URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="new-model-purchase-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Purchase Link / Info Page URL (Optional)
                  </Label>
                  <Input
                    id="new-model-purchase-url"
                    type="text"
                    placeholder="https://example.com/buy-this-model"
                    value={newModelPurchaseUrl}
                    onChange={(e) => setNewModelPurchaseUrl(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Allow tickets after warranty expiration */}
                <div className="flex items-center gap-2.5 py-1 md:pt-7">
                  <input
                    id="new-model-allow-warranty-tickets"
                    type="checkbox"
                    checked={newModelAllowOutOfWarrantyTickets}
                    onChange={(e) => setNewModelAllowOutOfWarrantyTickets(e.target.checked)}
                    className="w-4 h-4 rounded text-primary border-slate-305 focus:ring-primary focus:ring-offset-0 transition-all cursor-pointer"
                    style={{ accentColor: brandColorAccent }}
                  />
                  <Label htmlFor="new-model-allow-warranty-tickets" className="text-xs font-bold uppercase tracking-wider text-slate-700 cursor-pointer select-none">
                    Allow Raising Tickets After Warranty Expiration
                  </Label>
                </div>

                {/* Description */}
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="new-model-desc" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Description (Optional)
                  </Label>
                  <textarea
                    id="new-model-desc"
                    rows={3}
                    placeholder="Enter model specifications..."
                    value={newModelDescription}
                    onChange={(e) => setNewModelDescription(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-slate-900"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsModelModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={savingModel}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {savingModel ? 'Saving...' : 'Add Model'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Model Modal */}
        <Dialog open={isEditModelModalOpen} onOpenChange={setIsEditModelModalOpen}>
          <DialogContent className="max-w-md md:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Edit Product Model</DialogTitle>
              <DialogDescription className="text-xs">
                Update model specification details.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpdateModel} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                {/* Category */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Category *
                  </Label>
                  <Select value={editModelCategoryId} onValueChange={setEditModelCategoryId}>
                    <SelectTrigger className="h-11 border-slate-300">
                      <SelectValue placeholder="Select product category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Model Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-model-name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Name *
                  </Label>
                  <Input
                    id="edit-model-name"
                    type="text"
                    placeholder="e.g. WindMax 100L"
                    value={editModelName}
                    onChange={(e) => setEditModelName(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                    required
                  />
                </div>

                {/* Model Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-model-number" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Number (Optional)
                  </Label>
                  <Input
                    id="edit-model-number"
                    type="text"
                    placeholder="e.g. WM-100L-2026"
                    value={editModelNumber}
                    onChange={(e) => setEditModelNumber(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Warranty Duration */}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-model-warranty-months" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Warranty Duration (Months) (Optional Override)
                  </Label>
                  <Input
                    id="edit-model-warranty-months"
                    type="number"
                    min="0"
                    placeholder="Leave empty to use category default"
                    value={editModelWarrantyMonths}
                    onChange={(e) => setEditModelWarrantyMonths(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Image Uploader & Preview */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Image (Catalog Photo)
                  </Label>
                  <div className="flex gap-4 items-center">
                    {editModelImageUrl ? (
                      <div className="relative w-16 h-16 border rounded bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={editModelImageUrl} alt="Preview" className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => setEditModelImageUrl('')}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 hover:bg-red-600 transition-colors"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 border-2 border-dashed bg-slate-50 border-slate-300 rounded flex items-center justify-center text-slate-400 shrink-0">
                        <Package size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        id="model-image-file-edit"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingImage(true);
                          try {
                            const url = await uploadApi.uploadFile(file);
                            setEditModelImageUrl(url);
                            toast.success('Model image uploaded successfully!');
                          } catch (err) {
                            console.error(err);
                            toast.error('Failed to upload model image');
                          } finally {
                            setUploadingImage(false);
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('model-image-file-edit').click()}
                        disabled={uploadingImage}
                        className="font-bold text-xs uppercase tracking-wider h-10 border-slate-300 bg-white"
                      >
                        <UploadSimple size={16} className="mr-1.5" />
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      <p className="text-[10px] text-slate-500 font-medium mt-1.5">
                        Accepts PNG, JPG, or GIF.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Model Image URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-model-image-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Model Image URL (Optional override)
                  </Label>
                  <Input
                    id="edit-model-image-url"
                    type="text"
                    placeholder="https://example.com/image.jpg"
                    value={editModelImageUrl}
                    onChange={(e) => setEditModelImageUrl(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Purchase URL */}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-model-purchase-url" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Purchase Link / Info Page URL (Optional)
                  </Label>
                  <Input
                    id="edit-model-purchase-url"
                    type="text"
                    placeholder="https://example.com/buy-this-model"
                    value={editModelPurchaseUrl}
                    onChange={(e) => setEditModelPurchaseUrl(e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* Allow tickets after warranty expiration */}
                <div className="flex items-center gap-2.5 py-1 md:pt-7">
                  <input
                    id="edit-model-allow-warranty-tickets"
                    type="checkbox"
                    checked={editModelAllowOutOfWarrantyTickets}
                    onChange={(e) => setEditModelAllowOutOfWarrantyTickets(e.target.checked)}
                    className="w-4 h-4 rounded text-primary border-slate-305 focus:ring-primary focus:ring-offset-0 transition-all cursor-pointer"
                    style={{ accentColor: brandColorAccent }}
                  />
                  <Label htmlFor="edit-model-allow-warranty-tickets" className="text-xs font-bold uppercase tracking-wider text-slate-700 cursor-pointer select-none">
                    Allow Raising Tickets After Warranty Expiration
                  </Label>
                </div>

                {/* Description */}
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="edit-model-desc" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Description (Optional)
                  </Label>
                  <textarea
                    id="edit-model-desc"
                    rows={3}
                    placeholder="Enter model specifications..."
                    value={editModelDescription}
                    onChange={(e) => setEditModelDescription(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-slate-900"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsEditModelModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={savingModel}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {savingModel ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Batch Import Wizard Dialog */}
        <Dialog open={isBatchModalOpen} onOpenChange={setIsBatchModalOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Batch Product Ingest Wizard</DialogTitle>
              <DialogDescription className="text-xs">
                Import multiple serialized products into your catalog at once.
              </DialogDescription>
            </DialogHeader>

            {/* Steps Indicator */}
            <div className="flex items-center justify-between border-y border-slate-100 py-3 my-4">
              {[
                { step: 1, label: 'Select Model' },
                { step: 2, label: 'Ingest Options' },
                { step: 3, label: 'Verification Sandbox' },
                { step: 4, label: 'Commit & Finish' }
              ].map((s) => (
                <div key={s.step} className="flex items-center gap-2">
                  <div 
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      batchStep === s.step 
                        ? 'text-white' 
                        : batchStep > s.step 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-500'
                    }`}
                    style={batchStep === s.step ? { backgroundColor: brandColorAccent } : {}}
                  >
                    {batchStep > s.step ? <CheckCircle size={16} weight="fill" /> : s.step}
                  </div>
                  <span className={`text-xs font-bold ${batchStep === s.step ? 'text-slate-950' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                  {s.step < 4 && <div className="h-[2px] w-8 bg-slate-100 hidden sm:block" />}
                </div>
              ))}
            </div>

            {/* STEP 1: MODEL SELECTION */}
            {batchStep === 1 && (
              <div className="space-y-6 py-2">
                {/* 1. Category Selector */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    1. Select Category *
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    {categories.map((cat) => {
                      const isActive = batchCategoryId === cat.id;
                      const count = models.filter((m) => m.category_id === cat.id).length;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setBatchCategoryId(cat.id);
                            setBatchModelId('');
                          }}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
                          style={{
                            borderColor: isActive ? brandColorAccent : '#e2e8f0',
                            backgroundColor: isActive ? `${brandColorAccent}10` : '#ffffff',
                            borderWidth: isActive ? '2px' : '1px',
                            boxShadow: isActive ? `0 4px 12px ${brandColorAccent}10` : 'none'
                          }}
                        >
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs"
                            style={{ 
                              backgroundColor: isActive ? brandColorAccent : '#f1f5f9',
                              color: isActive ? '#ffffff' : '#475569'
                            }}
                          >
                            {cat.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900 leading-tight">
                              {cat.name}
                            </p>
                            <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                              {count} {count === 1 ? 'model' : 'models'}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Model Selector */}
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    2. Select Model *
                  </Label>
                  {!batchCategoryId ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-slate-400 font-bold text-xs uppercase tracking-wider bg-slate-50/50">
                      Please select a category above to view its models
                    </div>
                  ) : (
                    <>
                      {models.filter((m) => m.category_id === batchCategoryId).length === 0 ? (
                        <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50/30">
                          <p className="text-sm font-bold text-slate-700">No models found for this category</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Go to the "Models" tab and create a model under this category first.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Search Input for Models */}
                          <div className="relative">
                            <MagnifyingGlass 
                              size={16} 
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                            />
                            <Input
                              placeholder="Search models in this category..."
                              value={batchModelSearch}
                              onChange={(e) => setBatchModelSearch(e.target.value)}
                              className="pl-9 h-9 border-slate-300 text-slate-900 bg-white"
                            />
                          </div>

                          {/* Scrollable Container with Fixed Height */}
                          <div className="max-h-[265px] overflow-y-auto pr-1.5 py-0.5 scrollbar-thin">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {models
                                .filter((m) => m.category_id === batchCategoryId)
                                .filter((m) => {
                                  if (!batchModelSearch.trim()) return true;
                                  const q = batchModelSearch.toLowerCase();
                                  return (
                                    (m.name || '').toLowerCase().includes(q) ||
                                    (m.model_number || '').toLowerCase().includes(q)
                                  );
                                })
                                .map((model) => {
                                  const isSelected = batchModelId === model.id;
                                  return (
                                    <button
                                      key={model.id}
                                      type="button"
                                      onClick={() => setBatchModelId(model.id)}
                                      className="relative group rounded-xl border text-left p-3.5 transition-all duration-200 flex flex-col justify-between hover:scale-[1.01] active:scale-[0.99] bg-white cursor-pointer"
                                      style={{
                                        borderColor: isSelected ? brandColorAccent : '#e2e8f0',
                                        borderWidth: isSelected ? '2px' : '1px',
                                        boxShadow: isSelected ? `0 4px 12px ${brandColorAccent}15` : 'none',
                                      }}
                                    >
                                      {isSelected && (
                                        <div 
                                          className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full flex items-center justify-center text-white shadow-md z-10"
                                          style={{ backgroundColor: brandColorAccent }}
                                        >
                                          <Check size={12} weight="bold" />
                                        </div>
                                      )}
                                      <div className="flex gap-3 items-start w-full">
                                        <div className="w-12 h-12 border border-slate-100 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                                          {model.image_url ? (
                                            <img src={model.image_url} alt={model.name} className="w-full h-full object-contain" />
                                          ) : (
                                            <Package size={20} className="text-slate-400" />
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-bold text-slate-900 truncate leading-snug">{model.name}</p>
                                          {model.model_number ? (
                                            <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold font-mono bg-slate-100 text-slate-700 rounded mt-1">
                                              {model.model_number}
                                            </span>
                                          ) : (
                                            <span className="inline-block px-1.5 py-0.5 text-[9px] font-medium text-slate-400 bg-slate-50 rounded mt-1 italic">
                                              No model number
                                            </span>
                                          )}
                                          <p className="text-[11px] text-slate-500 line-clamp-2 mt-1.5 font-medium leading-normal">
                                            {model.description || 'No description available for this model.'}
                                          </p>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 mt-6">
                  <Button
                    onClick={() => {
                      const selectedModel = models.find(m => m.id === batchModelId);
                      const selectedCategory = categories.find(c => c.id === batchCategoryId);
                      
                      let warrMonths = 12;
                      if (selectedModel && selectedModel.warranty_months !== undefined && selectedModel.warranty_months !== null) {
                        warrMonths = selectedModel.warranty_months;
                      } else if (selectedCategory && selectedCategory.warranty_months !== undefined && selectedCategory.warranty_months !== null) {
                        warrMonths = selectedCategory.warranty_months;
                      }
                      
                      // Calculate default manufacture date (today if empty)
                      const refDateStr = seqManufactureDate || new Date().toISOString().split('T')[0];
                      if (!seqManufactureDate) {
                        setSeqManufactureDate(refDateStr);
                      }
                      
                      const refDate = new Date(refDateStr);
                      if (!isNaN(refDate.getTime())) {
                        refDate.setMonth(refDate.getMonth() + warrMonths);
                        setSeqWarrantyExpiry(refDate.toISOString().split('T')[0]);
                      }
                      
                      setBatchStep(2);
                    }}
                    disabled={!batchCategoryId || !batchModelId}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}
                    {/* STEP 2: AUTOMATED SEQUENCE GENERATION */}
            {batchStep === 2 && (() => {
              const getSuffix = (index) => {
                if (seqPadding === 'none') return String(index);
                if (seqPadding === '01') return String(index).padStart(2, '0');
                if (seqPadding === '001') return String(index).padStart(3, '0');
                if (seqPadding === '0001') return String(index).padStart(4, '0');
                return String(index);
              };
              const previewFirst = seqPrefix ? `${seqPrefix}${getSuffix(parseInt(seqStartIndex, 10) || 1)}` : '—';
              const previewLast = seqPrefix ? `${seqPrefix}${getSuffix((parseInt(seqStartIndex, 10) || 1) + (parseInt(seqQuantity, 10) || 1) - 1)}` : '—';
              
              return (
                <div className="space-y-4 py-2">
                  <div className="space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
                        Live Sequence Preview
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-xs font-mono font-bold bg-white p-3 rounded border border-slate-150">
                        <div>
                          <span className="text-slate-400 font-sans block text-[10px] uppercase font-bold tracking-wider mb-0.5">First Serial</span>
                          <span className="text-slate-800">{previewFirst}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-sans block text-[10px] uppercase font-bold tracking-wider mb-0.5">Last Serial</span>
                          <span className="text-slate-800">{previewLast}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-2 font-medium">
                        Based on prefix, start index, quantity, and padding width of {seqPadding === 'none' ? 'no digits' : `${seqPadding.length} digits`}.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="seq-prefix" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Sequence Prefix *
                        </Label>
                        <Input
                          id="seq-prefix"
                          type="text"
                          placeholder="e.g. WIND1345-04062026-"
                          value={seqPrefix}
                          onChange={(e) => setSeqPrefix(e.target.value)}
                          className="h-11 border-slate-300 text-slate-900 font-mono font-bold"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Suffix Padding Width
                        </Label>
                        <Select value={seqPadding} onValueChange={setSeqPadding}>
                          <SelectTrigger className="h-11 border-slate-300">
                            <SelectValue placeholder="Select padding..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Padding (e.g. 1, 2, 10)</SelectItem>
                            <SelectItem value="01">2 Digits Padding (e.g. 01, 02, 10)</SelectItem>
                            <SelectItem value="001">3 Digits Padding (e.g. 001, 002, 010)</SelectItem>
                            <SelectItem value="0001">4 Digits Padding (e.g. 0001, 0002, 0010)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="seq-start" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Starting Number (Index) *
                        </Label>
                        <Input
                          id="seq-start"
                          type="number"
                          min="0"
                          placeholder="e.g. 1"
                          value={seqStartIndex}
                          onChange={(e) => setSeqStartIndex(Math.max(0, parseInt(e.target.value, 10) || 0))}
                          className="h-11 border-slate-300 text-slate-900"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="seq-qty" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Batch Quantity (Total to Generate) *
                        </Label>
                        <Input
                          id="seq-qty"
                          type="number"
                          min="1"
                          max="1000"
                          placeholder="e.g. 100"
                          value={seqQuantity}
                          onChange={(e) => setSeqQuantity(Math.max(1, parseInt(e.target.value, 10) || 0))}
                          className="h-11 border-slate-300 text-slate-900"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="seq-man-date" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Default Manufacture Date
                        </Label>
                        <Input
                          id="seq-man-date"
                          type="date"
                          value={seqManufactureDate}
                          onChange={(e) => {
                            const newDateStr = e.target.value;
                            setSeqManufactureDate(newDateStr);
                            if (newDateStr) {
                              const selectedModel = models.find(m => m.id === batchModelId);
                              const selectedCategory = categories.find(c => c.id === batchCategoryId);
                              
                              let warrMonths = 12;
                              if (selectedModel && selectedModel.warranty_months !== undefined && selectedModel.warranty_months !== null) {
                                warrMonths = selectedModel.warranty_months;
                              } else if (selectedCategory && selectedCategory.warranty_months !== undefined && selectedCategory.warranty_months !== null) {
                                warrMonths = selectedCategory.warranty_months;
                              }
                              
                              const refDate = new Date(newDateStr);
                              if (!isNaN(refDate.getTime())) {
                                refDate.setMonth(refDate.getMonth() + warrMonths);
                                setSeqWarrantyExpiry(refDate.toISOString().split('T')[0]);
                              }
                            }
                          }}
                          className="h-11 border-slate-300 text-slate-900"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="seq-warr-date" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          Default Warranty Expiry (Optional)
                        </Label>
                        <Input
                          id="seq-warr-date"
                          type="date"
                          value={seqWarrantyExpiry}
                          onChange={(e) => setSeqWarrantyExpiry(e.target.value)}
                          className="h-11 border-slate-300 text-slate-900"
                        />
                      </div>
                    </div>

                    <div className="bg-amber-50/50 border border-amber-200/60 rounded-lg p-3 text-[11px] text-amber-800 font-medium">
                      Note: Generated items will be loaded directly into the sandbox grid where you can double check their details, add specific deviations, or commit them to the live catalog registry.
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setBatchStep(1)}
                      className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleGenerateSequence}
                      className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                      style={{ backgroundColor: brandColorAccent }}
                    >
                      Generate & Validate
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* STEP 3: VERIFICATION SANDBOX */}
            {batchStep === 3 && (
              <div className="space-y-4 py-2">
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded border border-slate-200">
                  <div>
                    <h3 className="text-xs font-bold text-slate-800">Verification Sandbox & Real-time Validation</h3>
                    <p className="text-[10px] text-slate-500">Correct any cell invalidations inline below. Calculated warranties use category fallback defaults.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runBatchValidation(batchItems)}
                    disabled={validatingBatch}
                    className="font-bold text-xs uppercase tracking-wider h-8 flex items-center gap-1.5"
                  >
                    <ArrowClockwise size={14} className={validatingBatch ? "animate-spin" : ""} />
                    {validatingBatch ? "Validating..." : "Re-Validate"}
                  </Button>
                </div>

                <div className="border rounded-md overflow-hidden bg-white max-h-96 overflow-y-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 font-bold uppercase text-slate-500">
                        <th className="py-2.5 px-4 w-12 text-center">Row</th>
                        <th className="py-2.5 px-4 w-1/3">Serial Number</th>
                        <th className="py-2.5 px-4">Manufacture Date</th>
                        <th className="py-2.5 px-4">Warranty Expiry</th>
                        <th className="py-2.5 px-4">Status</th>
                        <th className="py-2.5 px-4 text-right w-12">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {batchItems.map((item, index) => {
                        const result = validationResults[index];
                        const isValid = result?.is_valid ?? true;
                        const errorMsg = result?.error_message;
                        const calculatedExpiry = result?.calculated_warranty_expiry;

                        return (
                          <tr key={index} className={!isValid ? "bg-red-50/20" : ""}>
                            <td className="py-2 px-4 text-center font-bold text-slate-400">{index + 1}</td>
                            <td className="py-2 px-4">
                              <Input
                                value={item.serial_number}
                                onChange={(e) => {
                                  handleUpdateBatchItemCell(index, 'serial_number', e.target.value);
                                }}
                                className={`h-8 text-xs font-mono ${!isValid ? "border-red-500 focus-visible:ring-red-500/20" : "border-slate-350"}`}
                              />
                            </td>
                            <td className="py-2 px-4">
                              <Input
                                type="date"
                                value={item.manufacture_date || ''}
                                onChange={(e) => handleUpdateBatchItemCell(index, 'manufacture_date', e.target.value)}
                                className="h-8 text-xs border-slate-350 bg-white"
                              />
                            </td>
                            <td className="py-2 px-4">
                              <Input
                                type="date"
                                value={item.warranty_expiry || ''}
                                onChange={(e) => handleUpdateBatchItemCell(index, 'warranty_expiry', e.target.value)}
                                className="h-8 text-xs border-slate-350 bg-white"
                                placeholder={calculatedExpiry ? formatDate(calculatedExpiry) : ""}
                              />
                              {calculatedExpiry && !item.warranty_expiry && (
                                <p className="text-[9px] text-slate-400 mt-0.5">
                                  Auto: {formatDate(calculatedExpiry)}
                                </p>
                              )}
                            </td>
                            <td className="py-2 px-4 font-bold text-xs">
                              {validatingBatch ? (
                                <span className="text-slate-400">checking...</span>
                              ) : isValid ? (
                                <span className="text-green-600 flex items-center gap-1 font-semibold">
                                  <CheckCircle size={14} weight="fill" /> Valid
                                </span>
                              ) : (
                                <span className="text-red-600 flex items-center gap-1 font-semibold" title={errorMsg}>
                                  <XCircle size={14} weight="fill" /> {result?.error_code === 'ALREADY_EXISTS' ? 'Duplicate DB' : result?.error_code === 'BATCH_DUPLICATE' ? 'Duplicate Batch' : 'Invalid'}
                                </span>
                              )}
                              {errorMsg && !validatingBatch && (
                                <p className="text-[9px] text-red-500 font-medium mt-0.5 leading-tight">{errorMsg}</p>
                              )}
                            </td>
                            <td className="py-2 px-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveBatchItem(index)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between pt-4 border-t border-slate-100 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setBatchStep(2)}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCommitBatch}
                    disabled={importingBatch || validatingBatch || validationResults.some(r => !r.is_valid)}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    {importingBatch ? 'Importing...' : 'Commit & Import'}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 4: COMMIT & FINISH */}
            {batchStep === 4 && (
              <div className="py-8 text-center space-y-4">
                <div className="w-16 h-16 bg-green-50 border border-green-200 text-green-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle size={40} weight="fill" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Batch Ingest Complete</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Successfully imported all validated serial numbers for model <strong className="text-slate-800">
                    {models.find(m => m.id === batchModelId)?.name}
                  </strong>. They are now registered in the global catalog.
                </p>

                <div className="pt-6 border-t border-slate-100 mt-6 flex justify-center">
                  <Button
                    onClick={() => setIsBatchModalOpen(false)}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-8 text-white"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    Done & Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* View Batch Details Modal */}
        <Dialog open={isViewBatchModalOpen} onOpenChange={setIsViewBatchModalOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black flex items-center gap-2">
                <Package size={24} className="text-primary" />
                Batch Details
              </DialogTitle>
              <DialogDescription className="text-xs">
                Review and manage individual serial numbers in this batch.
              </DialogDescription>
            </DialogHeader>

            {selectedBatch && (
              <div className="space-y-6 mt-4">
                {/* Batch Summary Panel */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs">
                  <div>
                    <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px]">Product Model</span>
                    <span className="font-bold text-slate-800 text-sm">{selectedBatch.model_name}</span>
                  </div>
                  <div>
                    <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px]">Total Pieces</span>
                    <span className="font-bold text-slate-800 text-sm">{selectedBatch.products.length} units</span>
                  </div>
                  <div>
                    <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px]">Mfg Date</span>
                    <span className="font-bold text-slate-850 text-sm">
                      {selectedBatch.manufacture_date ? formatDate(selectedBatch.manufacture_date) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px]">Warranty Expiry</span>
                    <span className="font-bold text-slate-850 text-sm">
                      {selectedBatch.warranty_expiry ? formatDate(selectedBatch.warranty_expiry) : '—'}
                    </span>
                  </div>
                </div>

                {/* Search / Filter Serial Numbers */}
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by serial number..."
                    value={batchSearchQuery}
                    onChange={(e) => setBatchSearchQuery(e.target.value)}
                    className="pl-9 h-10 text-xs border-slate-300 text-slate-900 bg-white"
                  />
                </div>

                {/* Individual Products Table */}
                <div className="border rounded-lg overflow-hidden bg-white max-h-[40vh] overflow-y-auto shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-500">
                        <th className="py-2.5 px-4">Serial Number</th>
                        <th className="py-2.5 px-4">Category</th>
                        <th className="py-2.5 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {selectedBatch.products
                        .filter(p => p.serial_number?.toLowerCase().includes(batchSearchQuery.toLowerCase()))
                        .map(prod => (
                          <tr key={prod.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="py-3 px-4 font-mono font-bold text-slate-800">{prod.serial_number}</td>
                            <td className="py-3 px-4 font-semibold text-slate-650">{getCategoryName(prod.category_id)}</td>
                            <td className="py-3 px-4 text-right space-x-1.5 whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[10px] px-2 font-bold uppercase tracking-wider"
                                onClick={() => {
                                  setIsViewBatchModalOpen(false);
                                  handleOpenEditModal(prod);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="h-7 text-[10px] px-2 font-bold uppercase tracking-wider text-white"
                                onClick={() => handleDeleteProduct(prod.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsViewBatchModalOpen(false)}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
